import { useEffect, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  defaultRestSecFor,
  type Exercise,
  REST_PICKER_MAX,
  REST_PICKER_MIN,
  REST_PICKER_STEP,
} from "@heraion/shared";
import {
  Button,
  Dialog,
  RestStepper,
  resolveTheme,
  useExerciseName,
} from "@heraion/ui";

import { useThemePalette } from "../theme";

// web-rnw's LoggerHostDeps.ExerciseTimerSheet (RW-18) — the per-exercise
// rest/hold override sheet the logger's gear icon opens. Same contract as
// apps/mobile/src/screens/ExerciseTimerSheet.tsx (props shape declared once
// in hostDeps.ts's ExerciseTimerSheetProps), rebuilt on shared primitives
// (Dialog + RestStepper + Button) instead of mobile's hand-rolled
// backdrop/card — RestStepper is already portable (JS-only stepper, no
// native slider), so only the modal chrome + action row needed a web mount.
type Props = {
  visible: boolean;
  exercise: Exercise | null;
  restGlobal: number;
  holdGlobal: number;
  saving: boolean;
  onSave: (restSec: number, holdSec: number) => void;
  onRemove: () => void;
  onCancel: () => void;
};

const REST_PRESETS = [30, 60, 90, 120, 180];
const HOLD_PRESETS = [15, 30, 45, 60, 90];

export function ExerciseTimerSheet({
  visible,
  exercise,
  restGlobal,
  holdGlobal,
  saving,
  onSave,
  onRemove,
  onCancel,
}: Props) {
  const { dark, family } = useThemePalette();
  const base = resolveTheme(dark, family);
  const theme = { ...base, name: dark ? ("dark" as const) : ("light" as const) };
  const { t } = useTranslation();
  const getExerciseName = useExerciseName();
  const styles = makeStyles();
  const [rest, setRest] = useState(restGlobal);
  const [hold, setHold] = useState(holdGlobal);
  const hasOverride =
    typeof exercise?.defaultRestSec === "number" ||
    typeof exercise?.defaultDurationSec === "number";

  useEffect(() => {
    if (!visible || !exercise) return;
    setRest(defaultRestSecFor(exercise.defaultRestSec, restGlobal));
    setHold(exercise.defaultDurationSec ?? holdGlobal);
  }, [visible, exercise, restGlobal, holdGlobal]);

  const field = (
    label: string,
    isDefault: boolean,
    value: number,
    onChange: (n: number) => void,
    presets: number[],
  ) => (
    <View style={styles.field}>
      <View style={styles.fieldHeader}>
        <Text style={[styles.fieldLabel, { color: theme.palette.ink }]}>{label}</Text>
        {isDefault ? (
          <Text style={[styles.fieldDefault, { color: theme.palette.ink3 }]}>
            {t("settings.timerDefaults.isDefault", { defaultValue: "default" })}
          </Text>
        ) : null}
      </View>
      <RestStepper
        value={value}
        min={REST_PICKER_MIN}
        max={REST_PICKER_MAX}
        step={REST_PICKER_STEP}
        onChange={onChange}
        disabled={saving}
        presets={presets}
        theme={theme}
      />
    </View>
  );

  return (
    <Dialog theme={theme} visible={visible} onRequestClose={onCancel} maxWidth={420}>
      <Text style={[styles.title, { color: theme.palette.ink }]}>
        {exercise ? getExerciseName(exercise.name) : ""}
      </Text>
      {field(
        t("settings.timerDefaults.restGlobal", { defaultValue: "Rest timer" }),
        rest === restGlobal,
        rest,
        setRest,
        REST_PRESETS,
      )}
      {field(
        t("settings.timerDefaults.holdGlobal", { defaultValue: "Hold timer" }),
        hold === holdGlobal,
        hold,
        setHold,
        HOLD_PRESETS,
      )}
      <Text style={[styles.hint, { color: theme.palette.ink3 }]}>
        {t("settings.timerDefaults.equalsDefaultHint", {
          defaultValue: "A value left equal to the global default is not saved as an override.",
        })}
      </Text>
      <View style={styles.actions}>
        {hasOverride ? (
          <Button
            theme={theme}
            variant="secondary"
            label={t("settings.timerDefaults.remove", { defaultValue: "Remove" })}
            onPress={onRemove}
            disabled={saving}
            style={styles.actionButton}
          />
        ) : null}
        <Button
          theme={theme}
          variant="secondary"
          label={t("mobile.logger.restEditCancel")}
          onPress={onCancel}
          disabled={saving}
          style={styles.actionButton}
        />
        <Button
          theme={theme}
          variant="primary"
          label={
            saving
              ? t("mobile.logger.restEditSaving")
              : t("mobile.logger.restEditSave")
          }
          onPress={() => onSave(rest, hold)}
          loading={saving}
          style={styles.actionButton}
        />
      </View>
    </Dialog>
  );
}

function makeStyles() {
  return StyleSheet.create({
    title: { fontSize: 16, fontWeight: "700" },
    field: { gap: 4 },
    fieldHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    fieldLabel: { fontSize: 15, fontWeight: "600" },
    fieldDefault: { fontSize: 13 },
    hint: { fontSize: 13, lineHeight: 18 },
    actions: { flexDirection: "row", gap: 8, marginTop: 8 },
    actionButton: { flex: 1 },
  });
}
